import { Button, Card, Pill } from '@salonomy/ui';
import type { StepId, StepStatus } from './goLive.js';
import { canGoLive, outstandingRequired } from './goLive.js';

const STEP_LABEL: Record<StepId, string> = {
  basics: 'Salon basics',
  bookMode: 'Client book',
  locations: 'Locations',
  team: 'Team',
  services: 'Services',
  hours: 'Working hours',
  queueSlug: 'Queue link',
};

/** Plain-English "what this unlocks" for each requirement — honesty brand, no jargon, no asterisks. */
const STEP_WHY: Record<StepId, string> = {
  basics: 'Your salon’s name, shown to clients everywhere.',
  bookMode: 'How client records are shared across the team.',
  locations: 'At least one site so the salon has an address and a place to put chairs.',
  team: 'At least one bookable person — the diary needs a chair to fill.',
  services: 'At least one treatment with a price, so bookings know what they’re for.',
  hours: 'Weekly hours for someone, so the diary knows when you’re open.',
  queueSlug: 'Your public queue link, salonomy.com/q/<slug>, for walk-ins.',
};

/**
 * WS6 brief item 7 — the go-live checklist. Reads REAL salon state (via the computed `steps`) and
 * shows what is still missing before a salon is operational. This is the WS3 funnel's landing target:
 * a fresh signup lands here and sees an honest "3 things left", each linking back into the real
 * screen that fixes it. When every required step is done, the salon is live.
 */
export function GoLiveChecklist({
  steps,
  slug,
  onJump,
}: {
  steps: StepStatus[];
  slug: string;
  onJump: (step: StepId) => void;
}) {
  const live = canGoLive(steps);
  const remaining = outstandingRequired(steps);
  // Optional (non-gating) steps still show their status, but they never block go-live.
  const required = steps.filter((s) => s.required);
  const optional = steps.filter((s) => !s.required);

  return (
    <Card>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}
      >
        <div>
          <div className="section-title">Go-live checklist</div>
          <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
            {live
              ? 'Everything’s in place — your salon is ready to take bookings.'
              : `${remaining} ${remaining === 1 ? 'thing' : 'things'} left before you can go live.`}
          </div>
        </div>
        <Pill tone={live ? 'success' : 'cobalt'} className="mono">
          {live ? 'Ready to go live' : `${remaining} left`}
        </Pill>
      </div>

      <div className="settings-list" style={{ marginTop: 12 }}>
        {required.map((s) => (
          <ChecklistRow key={s.id} step={s} onJump={onJump} />
        ))}
      </div>

      {live && slug ? (
        <div
          className="muted"
          style={{
            fontSize: 13,
            background: 'var(--slm-porcelain)',
            border: '1px solid var(--slm-border)',
            borderRadius: 8,
            padding: '10px 12px',
            marginTop: 14,
          }}
        >
          Your public queue link is live at{' '}
          <span className="mono" style={{ fontWeight: 600 }}>
            salonomy.com/q/{slug}
          </span>
          . Share it so walk-ins can take a ticket and watch their place from their phone.
        </div>
      ) : null}

      {optional.length > 0 ? (
        <>
          <div className="section-title" style={{ fontSize: 15, margin: '18px 0 2px' }}>
            Setup choices
          </div>
          <div className="muted" style={{ fontSize: 12.5, marginBottom: 8 }}>
            Already set from your salon, but worth confirming.
          </div>
          <div className="settings-list">
            {optional.map((s) => (
              <ChecklistRow key={s.id} step={s} onJump={onJump} />
            ))}
          </div>
        </>
      ) : null}
    </Card>
  );
}

function ChecklistRow({ step, onJump }: { step: StepStatus; onJump: (step: StepId) => void }) {
  return (
    <div className="settings-row">
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <span
          aria-hidden
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 22,
            height: 22,
            borderRadius: 999,
            flexShrink: 0,
            marginTop: 1,
            fontSize: 13,
            fontWeight: 700,
            color: step.done ? '#fff' : 'var(--slm-muted)',
            background: step.done ? 'var(--slm-success)' : 'transparent',
            border: step.done ? 'none' : '1.5px solid var(--slm-border)',
          }}
        >
          {step.done ? '✓' : ''}
        </span>
        <div>
          <div style={{ fontWeight: 600 }}>{STEP_LABEL[step.id]}</div>
          <div className="muted" style={{ fontSize: 12.5 }}>
            {STEP_WHY[step.id]}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
        {step.done ? (
          <Pill tone="success">Done</Pill>
        ) : (
          <Pill tone="neutral" className="mono">
            To do
          </Pill>
        )}
        <Button size="sm" variant="ghost" onClick={() => onJump(step.id)}>
          {step.done ? 'Review' : 'Set up'}
        </Button>
      </div>
    </div>
  );
}
